import { useEffect } from "react";
import { absoluteUrl } from "@/lib/seo";

/**
 * Sets document.title, meta description, OG/Twitter title+description, and
 * the canonical URL for the current route. Restores previous values on
 * unmount so navigating away doesn't leave stale metadata.
 *
 * @param {object} args
 * @param {string} args.title
 * @param {string} args.description
 * @param {string} [args.path], route path (e.g. "/faq") used for canonical + og:url
 */
export function useMeta({ title, description, path }) {
  useEffect(() => {
    if (!title && !description && !path) return;

    const prevTitle = document.title;

    const metaSelectors = [
      'meta[name="description"]',
      'meta[property="og:title"]',
      'meta[property="og:description"]',
      'meta[property="og:url"]',
      'meta[name="twitter:title"]',
      'meta[name="twitter:description"]',
    ];
    const prevMeta = metaSelectors.map((sel) => {
      const el = document.querySelector(sel);
      return { sel, el, value: el ? el.getAttribute("content") : null };
    });

    // Canonical link, create if missing so we can always control it
    let canonical = document.querySelector('link[rel="canonical"]');
    const canonicalWasCreated = !canonical;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    const prevCanonicalHref = canonical.getAttribute("href");

    if (title) document.title = title;

    const setMeta = (selector, content) => {
      if (!content) return;
      const el = document.querySelector(selector);
      if (el) el.setAttribute("content", content);
    };

    if (description) setMeta('meta[name="description"]', description);
    if (title) setMeta('meta[property="og:title"]', title);
    if (description) setMeta('meta[property="og:description"]', description);
    if (title) setMeta('meta[name="twitter:title"]', title);
    if (description) setMeta('meta[name="twitter:description"]', description);

    if (path) {
      const abs = absoluteUrl(path);
      canonical.setAttribute("href", abs);
      setMeta('meta[property="og:url"]', abs);
    }

    return () => {
      document.title = prevTitle;
      prevMeta.forEach(({ el, value }) => {
        if (el && value !== null) el.setAttribute("content", value);
      });
      if (canonicalWasCreated) {
        if (canonical.parentNode) canonical.parentNode.removeChild(canonical);
      } else if (prevCanonicalHref !== null) {
        canonical.setAttribute("href", prevCanonicalHref);
      }
    };
  }, [title, description, path]);
}
